import { Theme, useMediaQuery } from '@mui/material';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  BooleanField,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  NumberInput,
  DateTimeInput,
  BooleanInput,
  ReferenceInput,
  AutocompleteInput,
  required,
  useRecordContext,
  FunctionField,
  SimpleList,
  Toolbar,
  SaveButton,
  DeleteButton,
  BulkDeleteButton,
} from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';

// 상품 정보 표시 필드
const ProductInfoField = () => {
  const record = useRecordContext();
  if (!record || !record.Product) return null;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {record.Product.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {record.Product.brand}
      </Typography>
    </Box>
  );
};

// 딜 상태 필드
const DealStatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const now = new Date();
  const endTime = record.deal_end_time ? new Date(record.deal_end_time) : null;
  const isExpired = endTime && endTime < now;
  const isActive = record.is_active;

  let status = '미활성';
  let color: 'default' | 'success' | 'error' = 'default';

  if (isActive && !isExpired) {
    status = '진행 중';
    color = 'success';
  } else if (isActive && isExpired) {
    status = '종료됨';
    color = 'error';
  }

  return <Chip label={status} color={color} size="small" />;
};

// 딜 특별 전시 목록
export const DealFeaturedList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <List
      sort={{ field: 'display_order', order: 'ASC' }}
      perPage={50}
      title="캣츄딜 관리"
      exporter={false}
    >
      {isSmall ? (
        <SimpleList
          primaryText={(record) => record.Product?.name || '상품 정보 없음'}
          secondaryText={(record) => {
            const now = new Date();
            const endTime = record.deal_end_time ? new Date(record.deal_end_time) : null;
            const isExpired = endTime && endTime < now;
            const isActive = record.is_active;
            let status = '미활성';
            if (isActive && !isExpired) status = '진행 중';
            else if (isActive && isExpired) status = '종료됨';

            return (
              <>
                <Typography variant="body2" component="span">
                  {status}
                </Typography>
                {endTime && (
                  <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                    (~{endTime.toLocaleDateString()})
                  </Typography>
                )}
              </>
            );
          }}
          tertiaryText={(record) => (
            <Typography variant="caption" color="text.secondary">
              순서: {record.display_order}
            </Typography>
          )}
          linkType="edit"
        />
      ) : (
        <Datagrid
          rowClick="edit"
          bulkActionButtons={
            <BulkDeleteButton
              confirmTitle="선택한 캣츄딜 삭제"
              confirmContent="선택한 항목들을 캣츄딜에서 삭제하시겠습니까?"
            />
          }
        >
          <FunctionField
            label="상품 정보"
            render={() => <ProductInfoField />}
          />

          <DateField
            source="deal_end_time"
            label="종료 시간"
            showTime
          />

          <FunctionField
            label="상태"
            render={() => <DealStatusField />}
          />

          <BooleanField source="is_active" label="활성화" />

          <DateField
            source="created_at"
            label="등록일"
            showTime
          />
        </Datagrid>
      )}
    </List>
  );
};

// 딜 특별 전시 상세
export const DealFeaturedShow = () => (
  <Show title="캣츄딜 상세">
    <SimpleShowLayout>
      <TextField source="id" label="ID" />

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>상품 정보</Typography>
      <ReferenceField source="product_id" reference="products" label="상품">
        <TextField source="name" />
      </ReferenceField>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>딜 설정</Typography>
      <NumberField source="display_order" label="표시 순서" />
      <DateField source="deal_end_time" label="딜 종료 시간" showTime />
      <BooleanField source="is_active" label="활성화 여부" />

      <FunctionField
        label="현재 상태"
        render={() => <DealStatusField />}
      />

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>시스템 정보</Typography>
      <DateField source="created_at" label="등록일" showTime />
      <DateField source="updated_at" label="수정일" showTime />
    </SimpleShowLayout>
  </Show>
);

// 커스텀 툴바 (삭제 시 상품명 표시)
const DealFeaturedEditToolbar = () => {
  const record = useRecordContext();
  const productName = record?.Product?.name || '상품';

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton />
      <DeleteButton
        confirmTitle="캣츄딜 삭제"
        confirmContent={`"${productName}"을(를) 캣츄딜에서 삭제하시겠습니까?`}
      />
    </Toolbar>
  );
};

// 딜 특별 전시 편집
export const DealFeaturedEdit = () => (
  <Edit title="캣츄딜 편집" mutationMode="pessimistic">
    <SimpleForm toolbar={<DealFeaturedEditToolbar />}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        캣츄딜 페이지에 표시될 상품의 설정을 변경할 수 있습니다.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <ReferenceInput source="product_id" reference="products" label="상품">
          <AutocompleteInput
            optionText="name"
            validate={required()}
            filterToQuery={searchText => ({ search: searchText })}
            shouldRenderSuggestions={(val: string) => val.trim().length > 1}
            noOptionsText="상품명을 2자 이상 입력하세요"
            debounce={500}
            fullWidth
            helperText="상품명을 2자 이상 입력하여 검색하세요"
          />
        </ReferenceInput>
      </Box>

      <Box sx={{ mb: 3 }}>
        <NumberInput
          source="display_order"
          label="표시 순서"
          helperText="작은 숫자가 먼저 표시됩니다"
          defaultValue={0}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <DateTimeInput
          source="deal_end_time"
          label="딜 종료 시간"
          helperText="딜이 종료되는 시간을 설정하세요 (타이머가 표시됩니다)"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <BooleanInput
          source="is_active"
          label="활성화"
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
        * 딜 종료 시간이 지나면 타이머가 자동으로 사라집니다.
      </Typography>
    </SimpleForm>
  </Edit>
);

// 딜 특별 전시 생성
export const DealFeaturedCreate = () => (
  <Create title="캣츄딜 추가">
    <SimpleForm>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        캣츄딜 페이지에 표시할 상품을 추가합니다.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <ReferenceInput source="product_id" reference="products" label="상품">
          <AutocompleteInput
            optionText="name"
            validate={required()}
            filterToQuery={searchText => ({ search: searchText })}
            shouldRenderSuggestions={(val: string) => val.trim().length > 1}
            noOptionsText="상품명을 2자 이상 입력하세요"
            debounce={500}
            fullWidth
            helperText="상품명을 2자 이상 입력하여 검색하세요"
          />
        </ReferenceInput>
      </Box>

      <Box sx={{ mb: 3 }}>
        <NumberInput
          source="display_order"
          label="표시 순서"
          helperText="작은 숫자가 먼저 표시됩니다"
          defaultValue={0}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <DateTimeInput
          source="deal_end_time"
          label="딜 종료 시간"
          helperText="딜이 종료되는 시간을 설정하세요 (타이머가 표시됩니다)"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <BooleanInput
          source="is_active"
          label="활성화"
          defaultValue={true}
        />
      </Box>
    </SimpleForm>
  </Create>
);
